import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileSpreadsheet, Users, Mail, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly display labels for known CRM statuses.
// Anything not in the map is shown as-is (with underscores → spaces).
const CONV_STATUS_LABELS = {
  initial_contact: 'Initial contact',
  in_discussion: 'In discussion',
  followup_needed: 'Follow-up needed',
  pledge_committed: 'Pledge committed',
  praying: 'Praying',
  no_response: 'No response',
  declined: 'Declined',
};
const labelFor = (s) =>
  CONV_STATUS_LABELS[s] || s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const triggerCsvDownload = async (url, fallbackName, onMessage) => {
  try {
    const res = await axios.get(url, { responseType: 'blob', withCredentials: true });
    const cd = res.headers['content-disposition'] || '';
    const match = /filename="?([^";]+)"?/.exec(cd);
    const filename = match ? match[1] : fallbackName;
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = blobUrl; a.download = filename;
    document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(blobUrl);
    onMessage?.(`Downloaded ${filename}`);
  } catch (err) {
    onMessage?.(err.response?.data?.detail || 'Download failed');
  }
};

export const ExportPanel = ({ onMessage }) => {
  const [segments, setSegments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSegment, setSelectedSegment] = useState('newsletter');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/exports/segments`, { withCredentials: true });
      setSegments(data);
    } catch (err) {
      onMessage?.(err.response?.data?.detail || 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div data-testid="export-panel-loading" className="p-4 text-sm text-slate-500">Loading export options…</div>;
  if (!segments) return null;

  const supSeg = segments.supporter_segments || {};
  const convStatuses = segments.conversation_statuses || [];

  return (
    <div data-testid="admin-export-panel"
         className="bg-gradient-to-br from-emerald-50 via-white to-white rounded-3xl border-2 border-emerald-200/60 shadow-sm p-6 mb-6">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2.5 rounded-2xl bg-emerald-100 flex-shrink-0">
          <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-900 font-['Outfit']">Exports & email lists</h2>
          <p className="text-sm text-slate-600 mt-0.5">
            Generate CSVs you can share with Wycliffe or use to send newsletters from another tool.
          </p>
        </div>
        <button
          data-testid="export-refresh"
          onClick={load}
          title="Refresh counts"
          className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ─── Wycliffe-evidence section ─── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-700" />
            <h3 className="font-bold text-slate-900">Supporter list (Wycliffe evidence)</h3>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Full sheet of contact + pledge details. Includes name, email, country, WhatsApp,
            pledge amount + currency, start date, type, and any personal message.
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-amber-50 rounded-xl px-3 py-2">
              <p className="text-slate-500">Pledged monthly</p>
              <p className="text-lg font-bold text-amber-700">{supSeg.pledged_monthly || 0}</p>
            </div>
            <div className="bg-emerald-50 rounded-xl px-3 py-2">
              <p className="text-slate-500">Active monthly</p>
              <p className="text-lg font-bold text-emerald-700">{supSeg.active_monthly || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-xl px-3 py-2">
              <p className="text-slate-500">One-time</p>
              <p className="text-lg font-bold text-slate-700">{supSeg.one_time || 0}</p>
            </div>
            <div className="bg-violet-50 rounded-xl px-3 py-2">
              <p className="text-slate-500">Prayer only</p>
              <p className="text-lg font-bold text-violet-700">{supSeg.prayer_only || 0}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              data-testid="export-supporters-pledged-active"
              onClick={() => triggerCsvDownload(
                `${API}/admin/exports/supporters.csv?statuses=pledged,active`,
                'supporters-pledged-active.csv', onMessage,
              )}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 shadow-sm"
            >
              <Download className="w-4 h-4" /> Pledged + Active
            </button>
            <button
              data-testid="export-supporters-all"
              onClick={() => triggerCsvDownload(
                `${API}/admin/exports/supporters.csv`,
                'supporters-all.csv', onMessage,
              )}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800"
            >
              <Download className="w-4 h-4" /> Everyone
            </button>
          </div>
        </div>

        {/* ─── Email lists ─── */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" />
            <h3 className="font-bold text-slate-900">Email lists (by segment)</h3>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">
            Pick a segment to download a Name + Email CSV — perfect for pasting into
            newsletter tools or bulk WhatsApp invites.
          </p>
          <select
            data-testid="export-emails-segment"
            value={selectedSegment}
            onChange={(e) => setSelectedSegment(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <optgroup label="Supporters">
              <option value="newsletter">Newsletter subscribers ({supSeg.newsletter || 0})</option>
              <option value="pledged_monthly">Pledged monthly ({supSeg.pledged_monthly || 0})</option>
              <option value="active_monthly">Active monthly ({supSeg.active_monthly || 0})</option>
              <option value="one_time">One-time givers ({supSeg.one_time || 0})</option>
              <option value="prayer_only">Prayer-only ({supSeg.prayer_only || 0})</option>
            </optgroup>
            {convStatuses.length > 0 && (
              <optgroup label="Conversations (CRM)">
                {convStatuses.map(s => (
                  <option key={s.status} value={`conversation:${s.status}`}>
                    {labelFor(s.status)} ({s.count})
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          <button
            data-testid="export-emails-download"
            onClick={() => triggerCsvDownload(
              `${API}/admin/exports/emails.csv?segment=${encodeURIComponent(selectedSegment)}`,
              `emails-${selectedSegment.replace(':', '-')}.csv`, onMessage,
            )}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 shadow-sm"
          >
            <Download className="w-4 h-4" /> Download email list
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;
